import { useMemo } from 'react';
import { useUserIdsOfEmploymentTypes } from './use-employees';

type Where = Record<string, unknown>;

/**
 * The People header filters as a gateway filter. Most columns are profile fields and pass through;
 * the ones that are not need translating:
 * - Skills (text) → the person has a skill whose saved name contains it;
 * - Sub-Category (ids) → the person has experience in one of them;
 * - Employee Type → the people whose current HR term has that type (read from the HR terms first).
 */
export const useEmployeeColumnWhere = (columnWhere: Where): Where => {
  const { Skills, SubCategory, EmploymentType, ...rest } = columnWhere;
  const types = useMemo(
    () => ((EmploymentType as { $in?: string[] } | undefined)?.$in ?? []) as string[],
    [EmploymentType]
  );
  const { data: userIds } = useUserIdsOfEmploymentTypes(types);

  return useMemo(() => {
    const where: Where = { ...rest };
    const text = (Skills as { $regex?: string } | undefined)?.$regex;
    if (text) where.Skills = { $some: { SkillName: { contains: text } } };
    const subCategoryIds = (SubCategory as { $in?: string[] } | undefined)?.$in;
    if (subCategoryIds?.length)
      where.SubCategoryExperiences = { $some: { SubCategoryId: { in: subCategoryIds } } };
    // Nobody matches until the HR terms are read.
    if (types.length > 0) where.UserId = { $in: userIds ?? [] };
    return where;
    // `rest` is a new object each render; the filter's own parts decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rest), Skills, SubCategory, types, userIds]);
};
